import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth, addLocalItem } from "@/contexts/AuthContext";
import { Flame, TrendingUp, Heart } from "lucide-react";

const MOODS = [
  { id: "happy", label: "Happy", desc: "Light and free", color: "#E5B95F" },
  { id: "calm", label: "Calm", desc: "Settled and still", color: "#4A6B53" },
  { id: "grateful", label: "Grateful", desc: "Open and thankful", color: "#D2A679" },
  { id: "anxious", label: "Anxious", desc: "On edge, racing thoughts", color: "#C86B55" },
  { id: "sad", label: "Sad", desc: "Heavy, low", color: "#8A9BB5" },
  { id: "angry", label: "Angry", desc: "Frustrated, tense", color: "#9A4D3B" },
  { id: "tired", label: "Tired", desc: "Drained, low energy", color: "#9B8FA3" },
  { id: "hopeful", label: "Hopeful", desc: "Looking forward", color: "#7DB89E" },
];

const MOOD_BLOBS = {
  happy: (c) => <svg viewBox="0 0 60 60" className="w-12 h-12"><path d="M30 6C42 6 52 14 53 26C54 38 46 52 30 54C14 52 6 38 7 26C8 14 18 6 30 6Z" fill={c} opacity="0.85"/><path d="M20 32Q25 38 30 38Q35 38 40 32" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round"/></svg>,
  calm: (c) => <svg viewBox="0 0 60 60" className="w-12 h-12"><path d="M30 5C44 7 56 16 54 30C52 44 42 54 30 55C18 54 8 44 6 30C4 16 16 7 30 5Z" fill={c} opacity="0.85"/><path d="M15 30Q22 25 30 30Q38 35 45 30" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round"/></svg>,
  grateful: (c) => <svg viewBox="0 0 60 60" className="w-12 h-12"><path d="M30 4C40 4 54 12 55 28C54 44 42 56 30 56C18 56 6 44 5 28C6 12 20 4 30 4Z" fill={c} opacity="0.85"/><path d="M30 22L33 28L30 34L27 28Z" stroke="white" strokeWidth="2" fill="none" strokeLinejoin="round"/></svg>,
  anxious: (c) => <svg viewBox="0 0 60 60" className="w-12 h-12"><path d="M28 5C42 3 55 14 54 28C53 42 44 55 30 55C16 55 5 42 6 28C7 14 14 7 28 5Z" fill={c} opacity="0.85"/><path d="M18 30L24 24L30 32L36 26L42 30" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  sad: (c) => <svg viewBox="0 0 60 60" className="w-12 h-12"><path d="M30 6C44 8 54 18 52 30C50 42 40 54 30 54C20 54 10 42 8 30C6 18 16 8 30 6Z" fill={c} opacity="0.85"/><path d="M20 34Q25 28 30 28Q35 28 40 34" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round"/></svg>,
  angry: (c) => <svg viewBox="0 0 60 60" className="w-12 h-12"><path d="M30 4C44 6 56 16 54 30C52 44 42 54 30 56C18 54 6 44 6 30C6 16 16 6 30 4Z" fill={c} opacity="0.85"/><path d="M18 28L24 34L30 26L36 34L42 28" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  tired: (c) => <svg viewBox="0 0 60 60" className="w-12 h-12"><path d="M30 5C42 5 54 14 54 28C54 42 44 55 30 55C16 55 6 42 6 28C6 14 18 5 30 5Z" fill={c} opacity="0.85"/><path d="M19 30Q24 26 30 30" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round"/><path d="M30 30Q36 26 41 30" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round"/></svg>,
  hopeful: (c) => <svg viewBox="0 0 60 60" className="w-12 h-12"><path d="M30 4C44 6 55 16 53 30C51 44 42 55 30 56C18 55 7 44 7 30C7 16 16 6 30 4Z" fill={c} opacity="0.85"/><path d="M30 20L32 26H38L33 30L35 36L30 32L25 36L27 30L22 26H28Z" stroke="white" strokeWidth="1.5" fill="none" strokeLinejoin="round"/></svg>,
};

const DURATION_OPTIONS = [
  { id: "today", label: "Just today" },
  { id: "few_days", label: "A few days" },
  { id: "weeks", label: "Weeks" },
  { id: "months", label: "Months" },
];

const FEELING_PROMPTS = [
  "What's the first word that comes to mind?",
  "If your feeling had a color, what would it be?",
  "In one word, how is your heart today?",
  "Name the feeling sitting with you right now.",
];

export default function CheckInPage() {
  const { promptAuth, isAuthenticated } = useAuth();
  const [selectedMood, setSelectedMood] = useState(null);
  const [feelingWord, setFeelingWord] = useState("");
  const [intensity, setIntensity] = useState(5);
  const [duration, setDuration] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [streak, setStreak] = useState({ streak: 0, total: 0 });
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [weeklyRecap, setWeeklyRecap] = useState(null);
  const [prompt] = useState(FEELING_PROMPTS[Math.floor(Math.random() * FEELING_PROMPTS.length)]);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem("sovha_welcome_seen")) {
      setShowWelcome(true);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [streakRes, checkinsRes, recapRes] = await Promise.all([
        api.get(`/check-in/streak`),
        api.get(`/check-ins`),
        api.get(`/check-in/weekly-recap`),
      ]);
      setStreak(streakRes.data);
      setRecentCheckins(checkinsRes.data.slice(0, 7));
      setWeeklyRecap(recapRes.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async () => {
    if (!selectedMood) return;
    const checkinData = { mood: selectedMood, feeling_word: feelingWord, intensity, duration };
    try {
      await api.post(`/check-in`, checkinData);
      setSubmitted(true);
      fetchData();
    } catch (e) {
      if (e.response?.status === 401) {
        // Save locally for session
        addLocalItem("sovha_check_ins", { ...checkinData, id: Date.now().toString(), timestamp: new Date().toISOString() });
        promptAuth("Save your check-in? Create a free account.", () => handleSubmit());
      } else {
        console.error(e);
      }
    }
  };

  const resetForm = () => {
    setSelectedMood(null);
    setFeelingWord("");
    setIntensity(5);
    setDuration("");
    setSubmitted(false);
  };

  if (submitted) {
    const mood = MOODS.find((m) => m.id === selectedMood);
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-16 sm:py-24" data-testid="checkin-success">
        <div className="bg-white rounded-[2rem] p-10 sm:p-14 text-center shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center text-3xl" style={{ backgroundColor: mood?.color + "20" }}>
            <span style={{ color: mood?.color }} className="font-heading text-2xl font-bold">{mood?.label?.[0]}</span>
          </div>
          <h2 className="font-heading text-3xl text-earth-800 mb-3">Thank you for checking in</h2>
          <p className="text-earth-600 mb-2">
            You're feeling <strong className="text-earth-800">{mood?.label}</strong>
            {feelingWord && (<> and your word is <strong className="text-earth-800">"{feelingWord}"</strong></>)}
            {duration && (<> <span className="text-earth-500">({DURATION_OPTIONS.find(d => d.id === duration)?.label?.toLowerCase()})</span></>)}
          </p>
          {streak.streak > 1 && (
            <div className="inline-flex items-center gap-2 bg-warmyellow/10 text-warmyellow px-4 py-2 rounded-full mt-4 mb-4">
              <Flame size={18} />
              <span className="font-semibold">{streak.streak}-day streak!</span>
              <span className="text-sm opacity-70">You're showing up for yourself.</span>
            </div>
          )}
          <p className="text-earth-500 text-sm mt-4 italic">"Naming what you feel is the first step to healing."</p>
          <button onClick={resetForm} data-testid="checkin-again-btn" className="mt-8 bg-terracotta text-earth-50 rounded-full px-8 py-3 font-medium hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
            Check in again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12 sm:py-20" data-testid="checkin-page">
      {/* Welcome Modal */}
      {showWelcome && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label="Welcome notice" data-testid="checkin-welcome-modal">
          <div className="absolute inset-0 bg-earth-800/30" />
          <div className="relative bg-white rounded-[2rem] p-10 max-w-sm w-full shadow-2xl animate-fade-in-up text-center z-10">
            <div className="w-14 h-14 rounded-full bg-sage-light mx-auto mb-5 flex items-center justify-center">
              <Heart size={24} className="text-sage" />
            </div>
            <h3 className="font-heading text-2xl text-earth-800 mb-3">This is your space.</h3>
            <p className="text-earth-600 text-sm leading-relaxed mb-8">
              We don't store or remember anything about you. Everything here stays between you and this moment.
            </p>
            <button
              onClick={() => { sessionStorage.setItem("sovha_welcome_seen", "true"); setShowWelcome(false); }}
              autoFocus
              data-testid="checkin-welcome-gotit"
              className="bg-sage text-earth-50 rounded-full px-8 py-3 font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-12">
        <p className="text-terracotta text-sm uppercase tracking-[0.15em] font-bold mb-3">Daily Check-in</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-earth-800 mb-4 tracking-tight">How are you today?</h1>
        <p className="text-earth-600 text-base max-w-lg mx-auto">No right or wrong answers. Just honesty with yourself.</p>
      </div>

      {/* Streak */}
      {streak.total > 0 && (
        <div className="flex items-center justify-center gap-4 mb-10" data-testid="streak-display">
          <div className="flex items-center gap-2 bg-warmyellow/10 px-4 py-2 rounded-full">
            <Flame size={18} className="text-warmyellow" />
            <span className="font-semibold text-earth-800">{streak.streak}-day streak</span>
          </div>
          <div className="flex items-center gap-2 bg-sage-light px-4 py-2 rounded-full">
            <TrendingUp size={18} className="text-sage" />
            <span className="font-semibold text-earth-800">{streak.total} total check-ins</span>
          </div>
        </div>
      )}

      {/* Weekly Recap Tag Cloud */}
      {weeklyRecap && weeklyRecap.top_moods && weeklyRecap.top_moods.length > 0 && (
        <div className="bg-white rounded-[2rem] p-6 sm:p-8 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 mb-8" data-testid="weekly-recap">
          <h4 className="font-heading text-lg text-earth-800 mb-4 text-center">Your 3 most common feelings this week</h4>
          <div className="flex flex-wrap justify-center gap-3">
            {weeklyRecap.top_moods.map((tm, i) => {
              const mood = MOODS.find(m => m.id === tm.mood);
              const sizes = ["text-2xl px-6 py-3", "text-xl px-5 py-2.5", "text-lg px-4 py-2"];
              return (
                <span key={tm.mood}
                  className={`rounded-full font-heading font-medium ${sizes[i] || sizes[2]}`}
                  style={{ backgroundColor: (mood?.color || "#8A918C") + "15", color: mood?.color || "#8A918C" }}
                  data-testid={`recap-mood-${tm.mood}`}
                >
                  {mood?.label || tm.mood} <span className="text-xs opacity-60">({tm.count})</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Mood Wheel */}
      <div className="bg-white rounded-[2rem] p-8 sm:p-12 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 mb-8">
        <h3 className="font-heading text-xl text-earth-800 mb-6 text-center">Pick your mood</h3>
        <div className="grid grid-cols-4 gap-4 max-w-lg mx-auto" data-testid="mood-wheel">
          {MOODS.map((mood) => {
            const isSelected = selectedMood === mood.id;
            const BlobSvg = MOOD_BLOBS[mood.id];
            return (
              <button key={mood.id} onClick={() => setSelectedMood(mood.id)} data-testid={`mood-select-${mood.id}`}
                className="flex flex-col items-center gap-1.5 p-3 sm:p-4 rounded-2xl transition-all duration-200"
                style={{
                  backgroundColor: mood.color + (isSelected ? "20" : "10"),
                  border: isSelected ? `2px solid ${mood.color}` : `1px solid ${mood.color}33`,
                  transform: isSelected ? "translateY(-2px) scale(1.02)" : undefined,
                  boxShadow: isSelected ? `0 6px 20px ${mood.color}25` : undefined,
                }}>
                <div className="transition-transform duration-200" style={{ transform: isSelected ? "scale(1.1)" : "scale(1)" }}>
                  {BlobSvg && BlobSvg(mood.color)}
                </div>
                <span className="text-xs font-semibold text-earth-800">{mood.label}</span>
                <span className="text-[10px] text-earth-500 leading-tight text-center">{mood.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Duration - How long has it been like this? */}
      {selectedMood && (
        <div className="bg-white rounded-[2rem] p-8 sm:p-12 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 mb-8 animate-fade-in-up" data-testid="duration-section">
          <h3 className="font-heading text-xl text-earth-800 mb-2 text-center">How long has it been like this?</h3>
          <p className="text-earth-500 text-sm text-center mb-6">Just roughly. There's no pressure to be exact.</p>
          <div className="flex flex-wrap justify-center gap-3">
            {DURATION_OPTIONS.map((opt) => (
              <button key={opt.id} onClick={() => setDuration(opt.id)} data-testid={`duration-${opt.id}`}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border ${
                  duration === opt.id ? "border-terracotta bg-terracotta-light text-terracotta" : "border-earth-200/50 bg-earth-100 text-earth-600 hover:bg-earth-200"
                }`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Feeling Word */}
      {selectedMood && (
        <div className="bg-white rounded-[2rem] p-8 sm:p-12 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 mb-8 animate-fade-in-up">
          <h3 className="font-heading text-xl text-earth-800 mb-2 text-center">{prompt}</h3>
          <p className="text-earth-500 text-sm text-center mb-6">Just one word. Whatever comes first.</p>
          <input type="text" value={feelingWord} onChange={(e) => setFeelingWord(e.target.value)} placeholder="Type your word..." data-testid="feeling-word-input"
            className="bg-earth-100 border-0 rounded-2xl px-6 py-4 text-earth-800 w-full max-w-sm mx-auto block text-center text-lg font-medium focus:ring-2 focus:ring-terracotta/30 focus:outline-none placeholder:text-earth-400" />
        </div>
      )}

      {/* Intensity */}
      {selectedMood && (
        <div className="bg-white rounded-[2rem] p-8 sm:p-12 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 mb-8 animate-fade-in-up">
          <h3 className="font-heading text-xl text-earth-800 mb-6 text-center">How intense is this feeling?</h3>
          <div className="max-w-sm mx-auto">
            <input type="range" min="1" max="10" value={intensity} onChange={(e) => setIntensity(Number(e.target.value))} data-testid="intensity-slider" className="w-full accent-terracotta" />
            <div className="flex justify-between text-xs text-earth-500 mt-1">
              <span>Gentle</span><span className="font-semibold text-earth-800 text-sm">{intensity}</span><span>Overwhelming</span>
            </div>
          </div>
        </div>
      )}

      {/* Submit */}
      {selectedMood && (
        <div className="text-center animate-fade-in-up">
          <button onClick={handleSubmit} data-testid="checkin-submit-btn" className="bg-terracotta text-earth-50 rounded-full px-10 py-4 font-medium text-lg hover:-translate-y-1 hover:shadow-lg transition-all duration-300">Check in</button>
        </div>
      )}

      {/* Recent Check-ins */}
      {recentCheckins.length > 0 && (
        <div className="mt-16" data-testid="recent-checkins">
          <h3 className="font-heading text-xl text-earth-800 mb-6 text-center">Your recent check-ins</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {recentCheckins.map((c) => {
              const mood = MOODS.find((m) => m.id === c.mood);
              return (
                <div key={c.id} className="flex items-center gap-2 px-4 py-2 rounded-full border border-earth-200/50" style={{ backgroundColor: mood?.color + "10" }}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: mood?.color + "30", color: mood?.color }}>{mood?.label?.[0]}</div>
                  <span className="text-sm text-earth-700">{mood?.label}</span>
                  <span className="text-xs text-earth-400">{new Date(c.timestamp).toLocaleDateString()}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
